import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Props as ButtonProps } from '#src/atoms/buttons/Button/index.tsx';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { LoadingButton } from '#src/atoms/buttons/LoadingButton/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { glossary } from '#src/lib/glossary.ts';

const Message = styled(BodyM)`
  text-align: center;
  font-weight: normal;
  margin-bottom: var(--double-unit);
`;

const Footer = styled(Vertical)`
  margin-top: var(--double-unit);
`;

type Props = {
  onConfirm: (() => void) | (() => Promise<void>);
  title?: ReactNode;
  subtitle?: ReactNode;
  message?: ReactNode;
  cancelCta?: ReactNode;
  cta?: ReactNode;
  ctaProps?: Omit<ButtonProps, 'size'> & { size?: never };
  open: boolean;
  onClose: () => void;
  fullScreen?: boolean;
  hideCancel?: boolean;
  disclaimer?: ReactNode;
};
export const ConfirmDialog = ({
  title,
  subtitle,
  message,
  onConfirm,
  cancelCta,
  cta,
  ctaProps,
  open,
  onClose,
  fullScreen,
  hideCancel = false,
  disclaimer,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const confirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      title={<div className="text-center">{title}</div>}
      footer={
        <Footer>
          <Horizontal center>
            {!hideCancel && (
              <Button
                disabled={loading}
                size="medium"
                onClick={onClose}
                color="quaternary"
                fullWidth
              >
                {cancelCta || <FormattedMessage {...glossary.close} />}
              </Button>
            )}
            <LoadingButton
              loading={loading}
              size="medium"
              color="primary"
              onClick={confirm}
              fullWidth
              {...ctaProps}
            >
              {cta || <FormattedMessage defaultMessage="Confirm" />}
            </LoadingButton>
          </Horizontal>
          {disclaimer}
        </Footer>
      }
    >
      <Vertical gap={2}>
        <Vertical gap={2}>
          {subtitle}
          {message || (
            <Message>
              <FormattedMessage defaultMessage="Are you sure?" />
            </Message>
          )}
        </Vertical>
      </Vertical>
    </Dialog>
  );
};
